import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FeedbackService } from '../../services/feedback.service';
import * as requests from '../../requests';
import { Feedback } from '../../models/feedback';
import { PlanogramService } from 'src/app/services/planogram.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EventsService } from 'src/app/services/events.service';
import { RolesService } from 'src/app/services/roles.service';
import { SystemRole } from 'src/app/models/roles';
import { EventType } from 'src/app/models/events';
import { FormsModule } from '@angular/forms';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { HasRoleDirective } from '../../directives/has-role.directive';

@Component({
    selector: 'app-create-feedback-modal',
    templateUrl: './create-feedback-modal.component.html',
    styleUrls: ['./create-feedback-modal.component.scss'],
    imports: [
        FormsModule,
        NgIf,
        NgFor,
        HasRoleDirective,
        NgClass,
    ],
})
export class CreateFeedbackModalComponent {
  SystemRole = SystemRole;
  @Input() feedbackId?: string;
  content = '';
  isFeedbackCompleted: boolean | null = null;
  isPlanogramApproved: boolean | null = null;
  selectedFiles: File[] = [];
  uploadProgress: Map<string, number> = new Map(); // track progress
  submitting = false;

  readonly allowedExtensions = [
    'pdf',
    'png',
    'jpg',
    'jpeg',
    'pdf',
    'webp',
    'docx',
    'csv',
    'xlsx',
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private planogramService: PlanogramService,
    private feedbackService: FeedbackService,
    private dialogService: DialogService,
    private eventsService: EventsService,
    private rolesService: RolesService
  ) {}

  onFileChange(event: any): void {
    const files = Array.from(event.target.files) as File[];
    const validFiles = files.filter((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return !!ext && this.allowedExtensions.includes(ext);
    });

    if (validFiles.length < files.length) {
      this.dialogService.showWarning(
        `Some files were not allowed.<br>Allowed types: ${this.allowedExtensions.join(
          ', '
        )}`,
        'Invalid Files'
      );
      event.target.value = '';
    }

    this.selectedFiles = validFiles;
  }

  submitFeedback(): void {
    const planogramId = this.planogramService.getPlanogram();

    if (!planogramId || (!this.canSubmit)) return;

    const request: requests.FeedbackCreateRequest = {
      planogramId,
      feedbackId: this.feedbackId,
      content: this.content.trim()
    };

    if(this.isFeedbackCompleted){
      request.tags = 'feedback-completed';
    }
    else if(this.isPlanogramApproved){
      request.tags = 'planogram-approved';
    }

    this.submitting = true;

    this.feedbackService.createFeedback(request).subscribe({
      next: (feedback) => {
        if (feedback) {
          // ✅ Track feedback creation or reply (External users only)
          if (this.rolesService.roles.value.includes(SystemRole.EXTERNAL)) {
            const eventType = this.feedbackId
              ? EventType.FEEDBACK_REPLIED
              : EventType.FEEDBACK_CREATED;

            this.eventsService
              .createEvent({
                planogramId,
                eventType,
                description: this.feedbackId
                  ? 'User replied to existing feedback.'
                  : 'User created feedback on planogram.',
                metadata: feedback.id
              })
              .subscribe();
          }

          if (this.selectedFiles.length > 0) {
            this.uploadFilesSequentially(feedback.id, feedback, planogramId);
          } else {
            this.dialogService.showSuccess(
              'Feedback created successfully',
              'Success'
            );
            this.activeModal.close(feedback);
          }
        }
      },
      error: () => (this.submitting = false),
      complete: () => (this.submitting = false),
    });
  }

  private async uploadFilesSequentially(
    feedbackId: string,
    feedback: Feedback,
    planogramId: string
  ): Promise<void> {
    let filesUploaded = 0;

    for (const file of this.selectedFiles) {
      this.uploadProgress.set(file.name, 0);

      await new Promise<void>((resolve) => {
        this.feedbackService.uploadAttachment(feedbackId, file).subscribe({
          next: (result) => {
            if (typeof result === 'number') {
              this.uploadProgress.set(file.name, result);
            } else {
              feedback.attachments = feedback.attachments || [];
              feedback.attachments.push(result);
              filesUploaded++;
            }
          },
          complete: () => resolve(),
        });
      });
    }

    if (
      filesUploaded > 0 &&
      this.rolesService.roles.value.includes(SystemRole.EXTERNAL)
    ) {
      this.eventsService
        .createEvent({
          planogramId,
          eventType: EventType.FEEDBACK_ATTACHMENTS_ADDED,
          description: `${filesUploaded} attachment(s) added to feedback.`,
          metadata: feedbackId
        })
        .subscribe();
    }

    this.dialogService.showSuccess('Feedback created successfully', 'Success');
    this.activeModal.close(feedback);
  }

   get canSubmit(): boolean {
    // Can submit if content exists, or one of the flags is set
    return (
      !!this.content.trim() ||
      this.isFeedbackCompleted === true ||
      this.isPlanogramApproved === true
    );
  }

  toggleFeedbackCompleted(): void {
    this.isFeedbackCompleted = this.isFeedbackCompleted ? null : true;
    if (this.isFeedbackCompleted) this.isPlanogramApproved = null;
  }

  togglePlanogramApproved(): void {
    this.isPlanogramApproved = this.isPlanogramApproved ? null : true;
    if (this.isPlanogramApproved) this.isFeedbackCompleted = null;
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
